import eventHandlers from './eventHandlers';
import dom, {ShowEvent} from "../../../../../../common/ts/core/dom";
import {
    EMAIL_RETENTION_POPUP_MOBILE_CLOSE_BUTTON,
    EMAIL_RETENTION_POPUP_MOBILE_CLOSE_ICON,
    EMAIL_RETENTION_POPUP_MOBILE_FORM,
    EMAIL_RETENTION_MOBILE_SAVE_ALERT_CTA,
    SAVE_SEARCH_BUTTON,
    EMAIL_RETENTION_DESKTOP_CLOSE_FLOATING,
    EMAIL_RETENTION_DESKTOP_FLOATING,
    EMAIL_RETENTION_POPUP_MOBILE
} from "./constants";
import {EventBus} from "../../../../../../common/ts/events/EventBus";
import {ShowRetentionCaptureModalRequestedEvent} from "./events";
import tracking from "../../../../../../common/ts/core/tracking";
import {FullPageModalCloseEvent} from "../../../../../../common/ts/events/Events";
import emailRetentionForm from "../form";
import {USER_SEARCH_SELECTOR} from "../../../../selectors";
import apiClassic from "../../../../apiClassic";
import {ALERT_CREATION_SECTION_MANAGE_MERGED} from "../form/constants";

const eventBus: EventBus = EventBus.getInstance();

function init({
    search,
    pageNumber,
    isPaid,
    toggleTrackingName,
    submitTrackingName,
    isDesktop,
}: {
    search: string,
    pageNumber: number,
    isPaid: boolean,
    toggleTrackingName: string,
    submitTrackingName: string,
    isDesktop: boolean,
}) {
    eventHandlers.init();
    emailRetentionForm.init(
        toggleTrackingName,
        submitTrackingName,
        pageNumber,
        isPaid,
        isDesktop,
    );

    registerClickEvents(search);
    registerScrollEvents();
}

function hasPreviousSuggestions() {
    const previousSuggestions = dom.selectAll('.email-retention-form__suggestions__inputs__label')
    return previousSuggestions && previousSuggestions.length > 0 || false
}

function registerClickEvents(search: string) {
    const saveSearchButtonElement = dom.select(SAVE_SEARCH_BUTTON);
    saveSearchButtonElement?.addEventListener('click', () => {
        eventBus.emit(new ShowRetentionCaptureModalRequestedEvent({
            search,
            hasPreviousSuggestions: hasPreviousSuggestions()
        }));

        eventBus.emit(new tracking.TrackAnalyticsEvent({
            name: 'email-retention-left-cta',
        }));

        eventBus.emit(new dom.UpdateDataAttributeEvent({
            elementSelector: "#js-email-retention-form",
            attributeName: "data-form-origin-opened-from",
            attributeValue: "desktop-top",
        }));
    });

    const saveSearchFloatingButtonElement = dom.select("#save-search-floating-button");
    saveSearchFloatingButtonElement?.addEventListener('click', () => {
        const previousSuggestions = dom.selectAll('.email-retention-form__suggestions__inputs__label')
        const hasPreviousSuggestions = previousSuggestions && previousSuggestions.length > 0 || false
        eventBus.emit(new ShowRetentionCaptureModalRequestedEvent({ search, hasPreviousSuggestions }));

        eventBus.emit(new tracking.TrackAnalyticsEvent({
            name: 'email-retention-right-cta',
        }));

        eventBus.emit(new dom.UpdateDataAttributeEvent({
            elementSelector: "#js-email-retention-form",
            attributeName: "data-form-origin-opened-from",
            attributeValue: "desktop-floating",
        }));
    });

    const closeRetentionModalButton = dom.select("#close-email-retention-button");
    closeRetentionModalButton?.addEventListener('click', () => {
        eventBus.emit(new FullPageModalCloseEvent({
            id: "email-retention-modal",
        }));

        eventBus.emit(new tracking.TrackAnalyticsEvent({
            name: 'email-retention-modal-close-cta',
        }));
    });

    const closeRetentionModalCrossIcon = dom.select(".js-wl-fullpage-modal-close");
    closeRetentionModalCrossIcon?.addEventListener('click', () => {
        eventBus.emit(new tracking.TrackAnalyticsEvent({
            name: 'email-retention-modal-close-x',
        }));
    });

    const closeFloatingEmailRetention = dom.select(EMAIL_RETENTION_DESKTOP_CLOSE_FLOATING);
    closeFloatingEmailRetention?.addEventListener('click', () => {
        eventBus.emit(new dom.HideEvent({selector: EMAIL_RETENTION_DESKTOP_FLOATING}));
        window.removeEventListener('scroll', handleScrollBottomEmail);
    });

    const goToMobileEmailRetentionFormButton = dom.select(EMAIL_RETENTION_MOBILE_SAVE_ALERT_CTA);
    goToMobileEmailRetentionFormButton?.addEventListener('click', () => {
        eventBus.emit(new tracking.TrackAnalyticsEvent({
            name: 'email-retention-bottom-cta',
        }));
        const emailRetentionMobileForm = dom.select("#email-retention-form--mobile");
        emailRetentionMobileForm?.scrollIntoView({behavior: 'smooth'});
    });

    const closeIconEmailRetentionPopupMobile = dom.select(EMAIL_RETENTION_POPUP_MOBILE_CLOSE_ICON);
    closeIconEmailRetentionPopupMobile?.addEventListener('click', () => {
        eventBus.emit(new dom.HideEvent({selector: EMAIL_RETENTION_POPUP_MOBILE}));
    });

    const closeButtonEmailRetentionPopupMobile = dom.select(EMAIL_RETENTION_POPUP_MOBILE_CLOSE_BUTTON);
    closeButtonEmailRetentionPopupMobile?.addEventListener('click', () => {
        eventBus.emit(new dom.HideEvent({selector: "#email-retention__popup-mobile"}));
    });

    const formEmailRetentionPopupMobile = dom.select(EMAIL_RETENTION_POPUP_MOBILE_FORM) as HTMLFormElement;
    formEmailRetentionPopupMobile?.addEventListener("submit", (event) => {
        event.preventDefault();
        const formData = new FormData(formEmailRetentionPopupMobile);
        const email = formData.get("email")?.toString();

        if (email) {
            eventBus.emit(apiClassic.events.createEmailAlert({
                    search,
                    email,
                }
            ));

            eventBus.emit(new tracking.TrackAmplitudeEvent({
                section: ALERT_CREATION_SECTION_MANAGE_MERGED.toString(),
                type: "SearchSaved",
                serpUrl: window.location.href,
            }));

            eventBus.emit(new dom.HideEvent({selector: "#email-retention__popup-mobile"}));
        }
    });
}

function registerScrollEvents() {
    registerScrollForTopEmail();
    registerScrollForBottomEmail();
    registerScrollForSaveAlertMobileButton();
}

function registerScrollForTopEmail() {
    const emailRetentionContainer = dom.select("#email-retention");
    if (emailRetentionContainer) {
        const emailRetention = emailRetentionContainer as HTMLElement;
        const filtersContainerHeight = dom.select(USER_SEARCH_SELECTOR)?.getBoundingClientRect().height;
        const headerHeight = dom.select("header")?.getBoundingClientRect().height;
        const emailToTopOriginal = emailRetentionContainer.getBoundingClientRect().top;
        let separation = 26;
        if (filtersContainerHeight) {
            separation += filtersContainerHeight;
        }
        if(headerHeight) {
            separation += headerHeight;
        }

        window.addEventListener('scroll', () => {
            const scrolledPixels = window.scrollY;
            if (scrolledPixels > (emailToTopOriginal - separation)) {
                emailRetention.classList.add("fixed");
                emailRetention.style.top = `${separation.toString()}px`;
            } else {
                emailRetention.classList.remove("fixed");
                emailRetention.style.top = "";
            }
        });
    }
}

function registerScrollForBottomEmail() {
    window.addEventListener('scroll', handleScrollBottomEmail);
}

function registerScrollForSaveAlertMobileButton() {
    const emailRetentionMobileForm = dom.select("#email-retention-form--mobile");
    if (emailRetentionMobileForm) {
        const saveAlertMobileButton = dom.select("#save-alert-cta");
        if (saveAlertMobileButton) {
            const observer = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        saveAlertMobileButton?.classList.add("hide");
                    } else {
                        saveAlertMobileButton?.classList.remove("hide");
                    }
                });
            });
            observer.observe(emailRetentionMobileForm);
        }
    }
}

function handleScrollBottomEmail() {
    const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
    const percentageScrolled = (window.scrollY / pageHeight) * 100;

    if (percentageScrolled >= 70) {
        eventBus.emit(new ShowEvent({selector: "#email-retention-floating"}));
    }
}

export default {
    init,
}
